import { WarningDiamondIcon } from "@xola/icons";
import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { AxiosError, AxiosRequestConfig } from "axios";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { RestoresSurvivor, Traveler } from "types";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { CustomerRecordSummary } from "@/components/Customers/Contacts/CustomerRecordSummary";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";

interface UnmergeCustomerRecordModalProps {
    contact: Traveler; // the surviving contact the merge was applied to
    mergeEntry?: Traveler; // the absorbed (merged-away) record to restore
    isOpen: boolean;
    onClose: () => void;
}

const TRANSFERRED_INFORMATION = [
    "Profile details (Name & Email)",
    "Payment information",
    "Waivers",
    "Complete booking history",
    "Customer notes",
];

interface EmailConflict {
    conflictingTraveler: { id: string; name: string; email: string };
}

export const UnmergeCustomerRecordModal = ({
    contact,
    mergeEntry,
    isOpen,
    onClose,
}: UnmergeCustomerRecordModalProps) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const [conflict, setConflict] = useState<EmailConflict>();

    const invalidateAndClose = () => {
        invalidate("users", contact.id);
        if (mergeEntry?.id) {
            invalidate("users", mergeEntry.id);
        }
        if (conflict?.conflictingTraveler.id) {
            invalidate("users", conflict.conflictingTraveler.id);
        }
        invalidate("sellers", seller.id);
        onClose();
    };

    // Unmerge targets the absorbed source itself: clearing its mergedInto reverses the merge.
    const { mutateAsync: unmerge, isLoading } = useMutation(async (resolveConflict?: "merge") => {
        const payload: Record<string, unknown> = { mergedInto: null, seller: seller.id };
        if (resolveConflict) {
            payload.resolveConflict = resolveConflict;
        }
        // Suppress the global 409 flash so this modal can handle the email-conflict case itself.
        return api.patch(`users/${mergeEntry?.id}`, payload, { skipResponseInterceptor: true } as AxiosRequestConfig);
    });

    const submit = async (resolveConflict?: "merge") => {
        // On conflict resolution the record goes to the claimant; otherwise to the restored (unmerged) record.
        const redirectId = resolveConflict ? conflict?.conflictingTraveler.id : mergeEntry?.id;
        await unmerge(resolveConflict);
        flash.success(resolveConflict ? "Merged into existing customer record" : "Contact record unmerged");
        setConflict(undefined);
        invalidateAndClose();
        if (redirectId) {
            history.push(`/customers/contacts/${redirectId}`);
        }
    };

    const handleConfirm = async () => {
        try {
            await submit();
        } catch (error) {
            // The server nests the ConflictException payload under `field`.
            const { response } = error as AxiosError<{ field?: { email?: string; reason?: string } & EmailConflict }>;
            const conflictData = response?.data?.field;
            if (response?.status === 409 && conflictData?.email === "unmerge_email_in_use") {
                setConflict({ conflictingTraveler: conflictData.conflictingTraveler });
                return;
            }
            // Reverse-order unmerge: this record's survivor is itself merged and must be restored first.
            if (response?.status === 400 && conflictData?.reason === "unmerge_blocked_survivor_merged") {
                flash.danger("Restore this record's surviving customer first, then unmerge the records within it.");
                return;
            }
            flash.danger("Could not unmerge the customer record. Please try again.");
        }
    };

    const handleMergeIntoExisting = async () => {
        try {
            await submit("merge");
        } catch {
            flash.danger("Could not merge into existing record. Please try again.");
        }
    };

    if (!mergeEntry) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} size={conflict ? "medium" : "huge"} onClose={onClose}>
            {conflict ? (
                <EmailConflictContent
                    email={mergeEntry.email}
                    isLoading={isLoading}
                    onCancel={() => {
                        setConflict(undefined);
                        onClose();
                    }}
                    onMergeIntoExisting={handleMergeIntoExisting}
                />
            ) : (
                <UnmergeConfirmContent
                    contact={contact}
                    mergeEntry={mergeEntry}
                    isLoading={isLoading}
                    onCancel={onClose}
                    onConfirm={handleConfirm}
                />
            )}
        </Modal>
    );
};

interface UnmergeConfirmContentProps {
    contact: Traveler; // the current merged (surviving) record
    mergeEntry: Traveler; // the record being restored
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const getRestoredSurvivor = (contact: Traveler, restoresSurvivor?: RestoresSurvivor) => {
    const restored = { name: contact.name, email: contact.email, phone: contact.phone };
    for (const [field, delta] of Object.entries(restoresSurvivor ?? {})) {
        const key = field as keyof typeof restored;
        if (restored[key] === delta.new) {
            restored[key] = delta.old ?? "";
        }
    }

    return restored;
};

const UnmergeConfirmContent = ({ contact, mergeEntry, isLoading, onCancel, onConfirm }: UnmergeConfirmContentProps) => {
    const restoredSurvivor = getRestoredSurvivor(contact, mergeEntry.restoresSurvivor);

    return (
        <>
            <Modal.Header>Unmerge Customer Record</Modal.Header>

            <Modal.Body className="flex-col">
                <div className="mb-6 flex items-start gap-4 rounded-md bg-yellow-lighter p-4">
                    <WarningDiamondIcon className="mt-1 shrink-0 text-2xl" />
                    <div>
                        <div className="mb-2">
                            The following information will be transferred to the unmerged record:
                        </div>
                        <ul className="list-inside list-disc">
                            {TRANSFERRED_INFORMATION.map((line) => (
                                <li key={line}>{line}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <h3 className="mb-2">Current Merged Customer Record</h3>
                        <CustomerRecordSummary
                            name={contact.name}
                            email={contact.email}
                            phone={contact.phone}
                            mergedAt={mergeEntry.mergedAt}
                        />
                    </div>
                    <div>
                        <h3 className="mb-2">New Unmerged Customer Records</h3>
                        <div className="space-y-6">
                            <CustomerRecordSummary
                                name={restoredSurvivor.name}
                                email={restoredSurvivor.email}
                                phone={restoredSurvivor.phone}
                            />
                            <CustomerRecordSummary
                                name={mergeEntry.name}
                                email={mergeEntry.email}
                                phone={mergeEntry.phone}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="flex justify-end space-x-4">
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                <SubmitButton isLoading={isLoading} onClick={onConfirm}>
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

interface EmailConflictContentProps {
    email: string;
    isLoading: boolean;
    onCancel: () => void;
    onMergeIntoExisting: () => void;
}

const EmailConflictContent = ({ email, isLoading, onCancel, onMergeIntoExisting }: EmailConflictContentProps) => {
    return (
        <>
            <Modal.Header>Error Merging Customer Record</Modal.Header>

            <Modal.Body>
                The email ID of the new unmerged record <strong>{email}</strong> is already in use. The email address
                now belongs to a different, existing customer record — created after this profile was originally merged.
                <br />
                Would you like to merge the unmerged profile into that existing customer record instead of creating a
                new one?
            </Modal.Body>

            <Modal.Footer className="flex justify-end space-x-4">
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                <SubmitButton isLoading={isLoading} onClick={onMergeIntoExisting}>
                    Merge into existing record
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
